import { upperCase } from 'lodash';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@config/axios/axios.config';
import { arrayQueryParamsSerializer } from '@util/serialization';
import { paths } from '@common/types';
import { stableKey } from '../util';

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

interface ITaskProps {
  task_id: string;
}

const taskRequest = async (props: ITaskProps) => {
  const response = axiosInstance.get<
    paths[`/task/{task_id}`]['get']['responses']['200']['content']['application/json']
  >(`/task/${props.task_id}`, {
    params: {
      timezone,
    },
  });
  const data = (await response).data;

  return data;
};

export const useTaskQuery = (props: ITaskProps) => {
  const query = useQuery({
    queryKey: ['task', props.task_id],
    queryFn: () => taskRequest(props),
  });

  return query;
};

type IPatientTasksListProps = NonNullable<
  paths[`/patient/{patient_id}/tasks`]['get']['parameters']['query']
> & { patient_id: string };

const patientTasksListRequest = async (props: IPatientTasksListProps) => {
  const response = axiosInstance.get<
    paths[`/patient/{patient_id}/tasks`]['get']['responses']['200']['content']['application/json']
  >(`/patient/${props.patient_id}/tasks`, {
    params: {
      timezone,
      page: props.page,
      limit: props.limit,
      sort_by: props.sort_by,
      ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      ...(props.programs &&
        props.programs.length > 0 && {
          programs: props.programs,
        }),
      ...(props.priorities &&
        !props.statuses?.includes('Completed') &&
        props.priorities?.length > 0 && {
          priorities: props.priorities,
        }),
      ...(props.due_date && {
        due_date: props.due_date,
      }),
      ...(props.statuses &&
        props.statuses?.length > 0 && {
          statuses: props.statuses,
        }),
      include_future_tasks: true,
      include_completed_tasks: true,
    },
    paramsSerializer: arrayQueryParamsSerializer,
  });
  const data = (await response).data;

  return data;
};

export const usePatientTasksListQuery = (props: IPatientTasksListProps) => {
  const stableProps = stableKey(props);

  const query = useQuery({
    queryKey: ['tasks', 'patient', stableProps],
    queryFn: () => patientTasksListRequest(props),
    refetchOnWindowFocus: false,
  });

  return query;
};

type ITaskListProps = NonNullable<
  paths[`/patient/tasks`]['get']['parameters']['query']
>;

const taskListRequest = async (props: ITaskListProps) => {
  const response = axiosInstance.get<
    paths[`/patient/tasks`]['get']['responses']['200']['content']['application/json']
  >(`/patient/tasks`, {
    params: {
      timezone,
      page: props.page,
      limit: props.limit,
      search: props.search,
      sort_by: props.sort_by,
      ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      ...(props.category && {
        category: props.category,
      }),
      ...(props.programs &&
        props.programs.length > 0 && {
          programs: props.programs,
        }),
      ...(props.priorities &&
        !props.statuses?.includes('Completed') &&
        props.priorities?.length > 0 && {
          priorities: props.priorities,
        }),
      ...(props.location_building_ids &&
        props.location_building_ids?.length > 0 && {
          location_building_ids: props.location_building_ids,
        }),
      ...(props.statuses &&
        props.statuses?.length > 0 && {
          statuses: props.statuses,
        }),
      ...(props.assignee_id && {
        assignee_id: props.assignee_id,
      }),
      ...(props.team_id && {
        team_id: props.team_id,
      }),
      ...(props.practice_id && {
        practice_id: props.practice_id,
      }),
      ...(props.partner_id && {
        partner_id: props.partner_id,
      }),
      ...(props.completion_interval && {
        completion_interval: props.completion_interval,
      }),
      ...(props.due_date && {
        due_date: props.due_date,
      }),
      ...(props.provider_ids && {
        provider_ids: props.provider_ids,
      }),
      ...(props.last_updated_after && {
        last_updated_after: props.last_updated_after,
      }),
      ...(props.task_names && {
        task_names: props.task_names,
      }),
      ...(props.order_categories && {
        order_categories: props.order_categories,
      }),
      ...(props.order_types && {
        order_types: props.order_types,
      }),
    },
    paramsSerializer: arrayQueryParamsSerializer,
  });
  const data = (await response).data;

  return data;
};

export const useTaskListQuery = (props: ITaskListProps) => {
  const stableProps = stableKey(props);

  const query = useQuery({
    queryKey: ['tasks', 'all', stableProps],
    queryFn: () => taskListRequest(props),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  return query;
};

type IMyTasksProps = NonNullable<paths[`/task`]['get']['parameters']['query']>;

const myTasksRequest = async (props: IMyTasksProps) => {
  const response = axiosInstance.get<
    paths[`/task`]['get']['responses']['200']['content']['application/json']
  >(`/task`, {
    params: {
      timezone,
      page: props.page,
      limit: props.limit,
      search: props.search,
      sort_by: props.sort_by,
      ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      ...(props.own_tasks_only !== undefined && {
        own_tasks_only: props.own_tasks_only,
      }),
      ...(props.include_completed_tasks !== undefined && {
        include_completed_tasks: props.include_completed_tasks,
      }),
      ...(props.category && {
        category: props.category,
      }),
      ...(props.programs &&
        props.programs?.length > 0 && {
          programs: props.programs,
        }),
      ...(props.priorities &&
        props.priorities?.length > 0 && {
          priorities: props.priorities,
        }),
      ...(props.location_building_ids &&
        props.location_building_ids?.length > 0 && {
          location_building_ids: props.location_building_ids,
        }),
      ...(props.statuses &&
        props.statuses?.length > 0 && {
          statuses: props.statuses,
        }),
      ...(props.due_date && {
        due_date: props.due_date,
      }),
      ...(props.assignee_id && {
        assignee_id: props.assignee_id,
      }),
      ...(props.partner_id && {
        partner_id: props.partner_id,
      }),
      ...(props.practice_id && {
        practice_id: props.practice_id,
      }),
      ...(props.provider_ids && {
        provider_ids: props.provider_ids,
      }),
      ...(props.last_updated_after && {
        last_updated_after: props.last_updated_after,
      }),
      ...(props.task_names && {
        task_names: props.task_names,
      }),
      ...(props.order_categories && {
        order_categories: props.order_categories,
      }),
      ...(props.order_types && {
        order_types: props.order_types,
      }),
      ...(props.team_id && {
        team_id: props.team_id,
      }),
    },
    paramsSerializer: arrayQueryParamsSerializer,
  });
  const data = (await response).data;

  return data;
};

export const useMyTasksQuery = (props: IMyTasksProps) => {
  const stableProps = stableKey(props);

  const query = useQuery({
    queryKey: ['tasks', 'mine', stableProps],
    queryFn: () => myTasksRequest(props),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  return query;
};

export type IMyWorklistMetadataProps = NonNullable<
  paths['/task/filters-metadata']['get']['parameters']['query']
>;

const myWorklistMetadataRequest = async (props: IMyWorklistMetadataProps) => {
  const response = axiosInstance.get<
    paths[`/task/filters-metadata`]['get']['responses']['200']['content']['application/json']
  >(`/task/filters-metadata`, {
    params: { ...props, timezone },
  });
  const data = (await response).data;

  return data;
};

export const useMyWorklistMetadataQuery = (props: IMyWorklistMetadataProps) => {
  const query = useQuery({
    queryKey: ['myWorklistMetadata', stableKey(props)],
    queryFn: () => myWorklistMetadataRequest(props),
  });

  return query;
};

const allWorklistMetadataRequest = async ({
  completed,
}: {
  completed?: boolean;
}) => {
  const response = axiosInstance.get<
    paths[`/patient/tasks/filters-metadata`]['get']['responses']['200']['content']['application/json']
  >(`/patient/tasks/filters-metadata`, {
    params: {
      timezone,
      completed,
    },
  });
  const data = (await response).data;

  return data;
};

export const useAllWorklistMetadataQuery = ({
  completed,
}: {
  completed: boolean;
}) => {
  const query = useQuery({
    queryKey: ['allWorklistMetadata', completed],
    queryFn: () => allWorklistMetadataRequest({ completed }),
  });

  return query;
};

interface IWorklistMetricsProps {
  own_tasks_only: boolean;
}

const worklistMetricsRequest = async (props: IWorklistMetricsProps) => {
  const response = axiosInstance.get<
    paths[`/task/metrics`]['get']['responses']['200']['content']['application/json']
  >(`/task/metrics`, {
    params: {
      own_tasks_only: props.own_tasks_only,
      timezone,
    },
  });
  const data = (await response).data;

  return data;
};

export const useWorklistMetricsQuery = (props: IWorklistMetricsProps) => {
  const query = useQuery({
    queryKey: ['tasks', 'metrics', props.own_tasks_only],
    queryFn: () => worklistMetricsRequest(props),
  });

  return query;
};

const customTaskMetadataRequest = async () => {
  const response = axiosInstance.get<
    paths[`/task/custom-task-metadata`]['get']['responses']['200']['content']['application/json']
  >(`/task/custom-task-metadata`);
  const data = (await response).data;

  return data;
};

export const useCustomTaskMetadataQuery = () => {
  const query = useQuery({
    queryKey: ['task', 'customTaskMetadata'],
    queryFn: () => customTaskMetadataRequest(),
  });

  return query;
};

const taskFilterOptionsRequest = async ({
  completed,
}: {
  completed?: boolean;
}) => {
  const response = axiosInstance.get<
    paths[`/task/filter-options`]['get']['responses']['200']['content']['application/json']
  >(`/task/filter-options`, { params: { completed } });
  const data = (await response).data;

  return data;
};

export const useTaskFilterOptionsQuery = ({
  completed,
}: {
  completed?: boolean;
}) => {
  const query = useQuery({
    queryKey: ['task', 'filterOptions', completed],
    queryFn: () => taskFilterOptionsRequest({ completed }),
  });

  return query;
};

//BEGIN: HISTORY SECTION

interface ITaskHistoryProps {
  page: number;
  limit: number;
  task_id: string;
  enabled: boolean;
}

const taskHistoryRequest = async (props: ITaskHistoryProps) => {
  const response = axiosInstance.get<
    paths[`/task/{task_id}/history`]['get']['responses']['200']['content']['application/json']
  >(`/task/${props.task_id}/history`, {
    params: {
      page: props.page,
      limit: props.limit,
    },
  });
  const data = (await response).data;

  return data;
};

export const useTaskHistoryQuery = ({
  task_id,
  ...props
}: ITaskHistoryProps) => {
  const query = useQuery({
    enabled: !!props.enabled,
    queryKey: ['task', task_id, 'history', stableKey(props)],
    queryFn: () => taskHistoryRequest({ task_id, ...props }),
  });

  return query;
};
//END: HISTORY SECTION

const worklistTablePreferencesRequest = async () => {
  const response = axiosInstance.get<
    paths[`/preferences/task-table`]['get']['responses']['200']['content']['application/json']
  >(`/preferences/task-table`);
  const data = (await response).data;

  return data;
};

export const useWorklistTablePreferencesQuery = () => {
  const query = useQuery({
    queryKey: ['task', 'table', 'preferences'],
    queryFn: () => worklistTablePreferencesRequest(),
  });

  return query;
};
